import { Button, Flex, Group, Input, Loader, Paper, Text } from "@mantine/core";
import { FC, useEffect, useState } from "react";
import { useForm, UseFormReturnType } from "@mantine/form";
import { fetchWithAuthentication } from "../utilities/FetchUtil";

export type ChatForm = UseFormReturnType<{
  displayName: string;
}>;

const ChangeDisplayName: FC = () => {
  const [resultText, setResultText] = useState<string | null>(null);
  const [currentDisplayName, setCurrentDisplayName] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState<boolean | null>(null);

  const form: ChatForm = useForm({
    mode: "controlled",
    initialValues: {
      displayName: "",
    },
  });

  const getDisplayName = async () => {
    setIsLoading(true);
    const response = await fetchWithAuthentication(
      "/chat/display-name",
      "GET",
      null,
    );
    if (response.ok) {
      const json = await response.json();
      const displayName = json.displayName;
      setCurrentDisplayName(displayName);
      setIsLoading(false);
    } else {
      setIsSuccessful(false);
      setResultText("Unable to fetch current display name.");
      setIsLoading(false);
    }
  };

  const changeDisplayName = async (newDisplayName: string) => {
    setIsLoading(true);
    setIsSuccessful(null);
    setResultText(null);
    const response = await fetchWithAuthentication(
      "/chat/display-name",
      "POST",
      { displayName: newDisplayName },
    );
    if (response.ok) {
      setIsSuccessful(true);
      const json = await response.json();
      const displayName = json.displayName;
      setCurrentDisplayName(displayName);
      setResultText(`Display name successfully changed to ${displayName}.`);
      setIsLoading(false);
    } else {
      setIsSuccessful(false);
      setResultText("Error while attempting to change display name.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDisplayName();
  }, []);

  return (
    <Flex justify="center" align="center" w="100%" h="75%">
      <Paper w={500} p="xl" shadow="xl">
        <Flex justify="center" align="center" direction="column" gap="md">
          <Text size="xl" fw={700}>
            Change Admin Display Name
          </Text>
          <Text>Your current display name is {currentDisplayName}.</Text>
          <form
            onSubmit={form.onSubmit((values) => {
              changeDisplayName(values.displayName).then(() => form.reset());
            })}
          >
            <Group m="md" justify="center">
              <Input
                key={form.key("displayName")}
                placeholder="Enter new display name"
                {...form.getInputProps("displayName")}
              />
              <Button type="submit" disabled={isLoading}>
                Submit
              </Button>
            </Group>
            <Flex justify="center" align="center" mih="5em">
              {isLoading && <Loader />}
              {resultText && !isLoading && (
                <Text c={isSuccessful ? "green" : "red"}>{resultText}</Text>
              )}
            </Flex>
          </form>
        </Flex>
      </Paper>
    </Flex>
  );
};

export default ChangeDisplayName;
